
import { z } from 'zod';

import { ChapterGoalsSchema } from './chapter-ideation';
import { ChapterStorylineSchema } from '@/domains/story-planning';
import {
  IssueSeverityEnum,
  IssueConfidenceEnum,
  IssueDomainEnum,
} from '@/ai/agents/issue-classification';

// =============================================================================
// CANONICAL SCHEMA
// =============================================================================

const PacingEnum = z.enum([
  'Slow & Reflective',
  'Moderate & Developing',
  'Fast & Action-Packed'
]);

// =============================================================================
// DRAFTING HISTORY SCHEMAS
// =============================================================================

/**
 * Defines the role of an agent in the drafting workflow.
 * Used to categorize history entries for filtering and display.
 */
export const AgentRoleEnum = z.enum([
  'drafter',           // Initial content creation (chapter-drafter-v2)
  'refiner',           // Content improvement (draft-refiner-v2, draft-refiner-iterative)
  'reviser',           // Targeted text revision (selection-reviser-v2)
  'prose-critic',      // Quality critique - inside the chapter (prose-flow, sensory, voice)
  'continuity-critic', // Consistency critique - outside the chapter (continuity checkers)
  'judge',             // Satisfaction decision (draft-judge)
]);

/**
 * A single entry in the drafting history.
 * Captures one agent invocation with its inputs and outputs.
 */
export const HistoryEntrySchema = z.object({
  // Identity
  id: z.string().describe('Unique identifier for this entry'),
  timestamp: z.string().datetime().describe('ISO timestamp when this entry was created'),

  // Agent identification
  agentId: z.string().describe('The agent ID (e.g., draft-chapter-v2, critique-prose-flow)'),
  agentRole: AgentRoleEnum.describe('The role this agent plays in the workflow'),
  model: z.string().describe('The AI model used (e.g., anthropic/claude-opus-4-5)'),

  // Input context
  userMessage: z.string().optional().describe('The user instruction/prompt that triggered this action, if any'),

  // Output - common to all agents
  commentary: z.string().describe('The agent commentary explaining decisions made or findings'),

  // Output - for content-producing agents only (drafter, refiner, reviser)
  content: z.string().optional().describe('The full chapter HTML content at this point, if this agent produces content'),

  // Output - for partial-content agents (prose-continuer, selection-reviser)
  generatedContent: z.string().optional().describe('The content actually generated by the agent (e.g., continuation fragment or revised text), as opposed to the full chapter snapshot in content'),
  selectedText: z.string().optional().describe('The original selected text that was revised, for revision agents only'),

  // Output - for evaluator agents only (critics, continuity checkers)
  rating: z.number().min(1).max(10).optional().describe('Quality rating from 1-10, if this agent evaluates'),
  findingsCount: z.number().optional().describe('Number of issues/findings identified'),
  mustFixCount: z.number().optional().describe('Number of must_fix severity issues'),

  // Output - for judge agent only
  satisfied: z.boolean().optional().describe('Whether the judge determined the draft is satisfactory'),
});

/**
 * The current state of the drafting session.
 * Provides quick access to key metrics without parsing all entries.
 */
export const DraftingStateSchema = z.object({
  phase: z.enum(['drafting', 'refining', 'evaluating', 'complete']).describe('Current workflow phase'),
  iterationCount: z.number().describe('Number of complete draft-evaluate-optimize cycles'),
  lastRating: z.number().optional().describe('Most recent evaluation rating'),
  satisfied: z.boolean().optional().describe('Whether the most recent judge was satisfied'),
});

/**
 * Complete drafting history for a chapter.
 * Replaces LastDraftInfo with accumulated history of all drafting actions.
 */
export const DraftingHistorySchema = z.object({
  // Session metadata
  sessionId: z.string().describe('Unique identifier for this drafting session'),
  startedAt: z.string().datetime().describe('ISO timestamp when drafting began'),
  lastUpdatedAt: z.string().datetime().describe('ISO timestamp of most recent update'),

  // The initial creative direction
  initialPrompt: z.string().optional().describe('The original user message that initiated this drafting session'),

  // Accumulated history
  entries: z.array(HistoryEntrySchema).describe('Chronological list of all agent invocations'),

  // Quick-access current state
  currentState: DraftingStateSchema.describe('Summary of current drafting state'),
});

// Legacy schema for backwards compatibility during migration
const LastDraftInfoSchema = z.object({
  summaryOfChoices: z.string().describe('A summary of the choices and decisions made during the last draft'),
  model: z.string().describe('The AI model used to generate the last draft'),
});

// =============================================================================
// CRITIQUE DATA SCHEMAS
// =============================================================================

/**
 * A persisted finding from a critique agent.
 * Uses the unified issue classification schema.
 */
export const PersistedFindingSchema = z.object({
  id: z.string().describe('Unique identifier for this finding'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  category: z.string().describe('The specific category of this finding from the unified taxonomy'),
  description: z.string().describe('Clear explanation of the issue or observation'),
  excerpt: z.string().describe('Direct quote from the text where this issue occurs'),
  suggestion: z.string().optional().describe('Specific suggestion for how to address this issue'),
  from: z.number().describe('Text position in the editor (from). 0 if position not found.'),
  to: z.number().describe('Text position in the editor (to). 0 if position not found.'),
  hasPosition: z.boolean().describe('Whether this finding has a valid position in the editor'),
  sourceAgentId: z.string().describe('ID of the agent that created this finding'),
  sourceAgentName: z.string().describe('Display name of the agent that created this finding'),
});

/**
 * A persisted strength from a critique agent.
 */
export const PersistedStrengthSchema = z.object({
  id: z.string().describe('Unique identifier for this strength'),
  text: z.string().describe('Description of what the draft does well'),
  sourceAgentId: z.string().describe('ID of the agent that identified this strength'),
  sourceAgentName: z.string().describe('Display name of the agent that identified this strength'),
});

/**
 * A persisted weakness from a critique agent.
 * Uses the unified issue classification schema.
 */
export const PersistedWeaknessSchema = z.object({
  id: z.string().describe('Unique identifier for this weakness'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  domain: IssueDomainEnum.describe('The broad domain this weakness falls under'),
  description: z.string().describe('Description of a high-level area where the draft needs improvement'),
  sourceAgentId: z.string().describe('ID of the agent that identified this weakness'),
  sourceAgentName: z.string().describe('Display name of the agent that identified this weakness'),
});

/**
 * Accumulated critique data for a chapter.
 * Stores findings, strengths, weaknesses, and ratings from multiple critique agents.
 */
export const CritiqueDataSchema = z.object({
  findings: z.array(PersistedFindingSchema).describe('Cumulative list of findings from all critics'),
  strengths: z.array(PersistedStrengthSchema).describe('Cumulative list of strengths from all critics'),
  weaknesses: z.array(PersistedWeaknessSchema).describe('Cumulative list of weaknesses from all critics'),
  acceptedFindings: z.array(PersistedFindingSchema).optional().describe('Findings the author addressed by running refine. Fed back to critics as positive signal.'),
  rejectedFindings: z.array(PersistedFindingSchema).optional().describe('Findings the author explicitly dismissed. Fed back to critics as a taste signal so they stop re-raising the same issue.'),
  acceptedWeaknesses: z.array(PersistedWeaknessSchema).optional().describe('Weaknesses the author addressed by running refine. Fed back to critics as positive signal.'),
  rejectedWeaknesses: z.array(PersistedWeaknessSchema).optional().describe('Weaknesses the author explicitly dismissed. Fed back to critics as a taste signal so they stop re-raising the same systemic note.'),
  ratings: z.record(z.string(), z.number().min(1).max(10)).optional().describe('Ratings keyed by agent ID (e.g., critique-prose-flow: 8)'),
  commentaries: z.record(z.string(), z.string()).optional().describe('Commentary text from each critic keyed by agent ID. Shown in UI so authors can read the critic\u2019s overall impression.'),
  lastUpdated: z.string().datetime().describe('ISO timestamp of most recent update'),
});

export const ChapterStatusEnum = z.enum([
  'Created',
  'Planned',
  'Being Written',
  'Snapshotted',
]);

// =============================================================================
// STALENESS SCHEMAS
// =============================================================================

/**
 * Plan fields snapshot at the time content was last saved. Compared against the
 * current plan to detect drift between plan and written content.
 */
export const PlanSnapshotSchema = z.object({
  keyEvents: z.string().optional(),
  chapterGoals: ChapterGoalsSchema.optional(),
  pacing: PacingEnum.optional(),
  povCharacterId: z.string().optional(),
  scenes: z.array(z.object({
    id: z.string(),
    sceneNumber: z.number().optional(),
    title: z.string(),
    keyBeats: z.string().optional(),
  })).optional(),
});

export type PlanSnapshot = z.infer<typeof PlanSnapshotSchema>;

export const ChapterSchema = z.object({
  id: z.string().describe('Unique identifier for the chapter'),
  number: z.number().describe('Sequential chapter number'),
  title: z.string().describe('The title of the chapter'),
  status: ChapterStatusEnum.optional().describe('The current workflow status of the chapter'),
  keyEvents: z.string().optional().describe('A description of the key events that occur in this chapter'),
  povCharacterId: z.string().optional().describe('ID of the character whose POV this chapter is written from'),
  povJustification: z.string().optional().describe('Explanation for why this POV character was chosen'),
  projectedWordCount: z.number().optional().describe('Planned word count for this chapter'),
  actualWordCount: z.number().optional().describe('Actual word count of the written chapter'),
  pacing: PacingEnum.optional().describe('The narrative pacing of the chapter'),
  chapterGoals: ChapterGoalsSchema.optional().describe('The narrative goals this chapter aims to accomplish'),
  content: z.string().optional().describe('The full written content of the chapter'),
  draftingHistory: DraftingHistorySchema.optional().describe('Complete history of all drafting actions for this chapter'),
  /** @deprecated Use draftingHistory instead. Kept for backwards compatibility during migration. */
  lastDraftInfo: LastDraftInfoSchema.optional().describe('Information about the last draft generation'),
  chapterSummary: z.string().optional().describe('A summary of the chapter content'),
  notes: z.string().optional().describe('Author notes or reminders about this chapter'),
  critiqueData: CritiqueDataSchema.nullish().describe('Accumulated findings and strengths from critique agents'),
  activeStorylines: z.array(ChapterStorylineSchema).optional().describe('Storylines active in this chapter with their tension levels'),
  isFinalized: z.boolean().optional().describe('Whether this chapter has been finalized/locked by the author'),

  // Snapshot status flags — set when snapshots are created/cleared
  hasPlotSnapshot: z.boolean().optional().describe('Whether a plot snapshot exists for this chapter'),
  hasCharacterSnapshots: z.boolean().optional().describe('Whether character snapshots exist for this chapter'),
  hasWorldElementSnapshots: z.boolean().optional().describe('Whether world element snapshots exist for this chapter'),
  hasThreadSnapshots: z.boolean().optional().describe('Whether plot thread snapshots exist for this chapter'),
  hasWeaveSnapshot: z.boolean().optional().describe('Whether a weave snapshot exists for this chapter'),
  hasContinuityExtraction: z.boolean().optional().describe(
    'Whether the continuity-extractor-v2 agent has run on this chapter. Used to gate ' +
    'extraction of later chapters — running out-of-order causes mis-attribution and ' +
    'leaves stale facts in the log.',
  ),

  // Staleness baselines (see domains/chapter-workspace/utils/chapter-staleness.ts)
  snapshotContentHash: z.string().optional().describe('Hash of normalized chapter content at the time snapshots were last generated. Used to detect snapshot staleness.'),
  snapshotContentWordCount: z.number().optional().describe('Word count of chapter content at snapshot time. Used to size content drift.'),
  draftPlanHash: z.string().optional().describe('Hash of plan fields at the time content was last saved. Used to detect plan-vs-content drift.'),
  draftPlanSnapshot: PlanSnapshotSchema.optional().describe('Plan field values at the time content was last saved. Used for field-level drift classification.'),

  // Staleness snooze (whole-chapter; resets automatically when hashes diverge)
  staleSnoozedContentHash: z.string().optional().describe('Content hash at the time the user dismissed a staleness alert.'),
  staleSnoozedPlanHash: z.string().optional().describe('Plan hash at the time the user dismissed a staleness alert.'),
  staleSnoozedAt: z.string().datetime().optional().describe('ISO timestamp when the staleness alert was last dismissed.'),
});

// =============================================================================
// SUBSET SCHEMAS
// =============================================================================

/**
 * Minimal chapter reference
 */
export const ChapterRefSchema = z.object({
  id: z.string().describe('Unique identifier for the chapter'),
  number: z.number().describe('Sequential chapter number'),
  title: z.string().describe('The title of the chapter'),
});

/**
 * Chapter context for AI agents (writing/drafting).
 * Includes content or summary based on recency.
 */
export const ChapterContextSchema = z.object({
  id: z.string().describe('Unique identifier for the chapter'),
  number: z.number().describe('Sequential chapter number'),
  title: z.string().describe('The title of the chapter'),
  keyEvents: z.string().optional().describe('A description of the key events that occur in this chapter'),
  chapterGoals: ChapterGoalsSchema.optional().describe('The narrative goals this chapter aims to accomplish'),
  pacing: PacingEnum.optional().describe('The narrative pacing of the chapter'),
  content: z.string().optional().describe('The full written content of the chapter'),
  summary: z.string().optional().describe('A summary of the chapter content'),
});

/**
 * Chapter metadata for planning agents (no content)
 */
export const ChapterMetadataSchema = z.object({
  id: z.string().describe('Unique identifier for the chapter'),
  number: z.number().describe('Sequential chapter number'),
  title: z.string().describe('The title of the chapter'),
  keyEvents: z.string().optional().describe('A description of the key events that occur in this chapter'),
  projectedWordCount: z.number().optional().describe('Planned word count for this chapter'),
  pacing: PacingEnum.optional().describe('The narrative pacing of the chapter'),
  chapterGoals: ChapterGoalsSchema.optional().describe('The narrative goals this chapter aims to accomplish'),
  povJustification: z.string().optional().describe('An explanation on the suggested POV character'),
});

// Type inference
export type Chapter = z.infer<typeof ChapterSchema>;
export type ChapterStatus = z.infer<typeof ChapterStatusEnum>;
export type ChapterRef = z.infer<typeof ChapterRefSchema>;
export type ChapterContext = z.infer<typeof ChapterContextSchema>;
export type ChapterMetadata = z.infer<typeof ChapterMetadataSchema>;
// ChapterGoals type is now exported from chapter-ideation.ts

// Drafting history types
export type AgentRole = z.infer<typeof AgentRoleEnum>;
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;
export type DraftingState = z.infer<typeof DraftingStateSchema>;
export type DraftingHistory = z.infer<typeof DraftingHistorySchema>;

// Critique data types
export type PersistedFinding = z.infer<typeof PersistedFindingSchema>;
export type PersistedStrength = z.infer<typeof PersistedStrengthSchema>;
export type PersistedWeakness = z.infer<typeof PersistedWeaknessSchema>;
export type CritiqueData = z.infer<typeof CritiqueDataSchema>;

// Snapshot type selection — used by the snapshot confirmation dialog
export type SnapshotTypeSelection = {
  plot: boolean;
  characters: boolean;
  worldElements: boolean;
  threads: boolean;
  weave: boolean;
  /** Whether to run the continuity-extractor-v2 agent. */
  continuity: boolean;
  /** When true, existing snapshots for unchecked types are deleted. */
  deleteUnchecked: boolean;
};
